import styled from 'styled-components';
import Modal from '../Modal';
import { useTranslation } from '../../hooks/useNavConfig';
import FeedbackHeader from './FeedbackHeader';
import RatingsStep from './RatingsStep';
import { FeedbackStep } from './FeedbackStep';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useVerticalNavSurvey from '../../hooks/useVerticalNavSurvey';
import { useCallback } from 'react';
import { feedbackModalConfig } from './config';
import { FeedbackStepTypes } from './constants';
import { FeedbackCompletionStep } from './FeedbackCompletionStep';
import { setDisplayFeedbackModal } from '../../reducers/AppConfig';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledContainer = styled.div.withConfig({
  displayName: "FeedbackModal__StyledContainer",
  componentId: "sc-19auw6q-0"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    width: '460px',
    minWidth: '300px',
    overflow: 'auto',
    background: theme.color['fill-surface-default'],
    borderRadius: theme.borderRadius['200']
  };
});
const StyledContent = styled.div.withConfig({
  displayName: "FeedbackModal__StyledContent",
  componentId: "sc-19auw6q-1"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    padding: `${theme.space['400']} ${theme.space['800']} ${theme.space['600']} !important`,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'center'
  };
});
const getHeaderText = (currentStep, rating) => {
  let headerText = 'nav.feedbackModal.generalSurveyQuestion.neutral';
  if (currentStep.type === FeedbackStepTypes.feedback && rating) {
    headerText = currentStep.getHeaderKey(rating);
  } else if (currentStep.type !== FeedbackStepTypes.feedback && currentStep.headerKey) {
    headerText = currentStep.headerKey;
  }
  return headerText;
};
const FeedbackContent = ({
  type,
  setRating,
  feedback,
  setFeedback,
  completeSurvey,
  setContactConsent,
  contactConsent,
  incrementSurveyStep,
  consentTextKey,
  hideIllustration
}) => {
  switch (type) {
    case FeedbackStepTypes.rating:
      return /*#__PURE__*/_jsx(RatingsStep, {
        setRating: setRating
      });
    case FeedbackStepTypes.feedback:
      return /*#__PURE__*/_jsx(FeedbackStep, {
        feedback: feedback,
        setFeedback: setFeedback,
        setContactConsent: setContactConsent,
        contactConsent: contactConsent,
        incrementSurveyStep: incrementSurveyStep,
        consentTextKey: consentTextKey
      });
    case FeedbackStepTypes.complete:
      return /*#__PURE__*/_jsx(FeedbackCompletionStep, {
        completeSurvey: completeSurvey,
        hideIllustration: hideIllustration
      });
    default:
      return null;
  }
};
const FeedbackModal = () => {
  const {
    displayFeedbackModal
  } = useAppSelector(state => state.appConfig);
  const {
    t
  } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    setRating,
    setFeedback,
    feedback,
    rating,
    activeStep,
    completeSurvey,
    setContactConsent,
    contactConsent,
    incrementSurveyStep,
    resetSurvey
  } = useVerticalNavSurvey({
    displayFeedbackModal
  });
  const closeOnClick = useCallback(() => {
    resetSurvey();
    dispatch(setDisplayFeedbackModal({
      displayFeedbackModal: undefined
    }));
    return;
  }, [dispatch, resetSurvey]);
  if (!displayFeedbackModal || activeStep === undefined) {
    return null;
  }
  const config = feedbackModalConfig[displayFeedbackModal];
  const currentStep = config.steps[activeStep];
  const headerText = t(getHeaderText(currentStep, rating));
  const consentTextKey = currentStep.type === FeedbackStepTypes.feedback ? currentStep.consentTextKey : 'nav.feedbackModal.consensus';
  const hideIllustration = currentStep.type === FeedbackStepTypes.complete && currentStep.hideIllustration;
  return /*#__PURE__*/_jsx(Modal, {
    children: /*#__PURE__*/_jsxs(StyledContainer, {
      "data-test-id": "vertical-nav-feedback-modal",
      children: [currentStep.type === FeedbackStepTypes.complete ? null : /*#__PURE__*/_jsx(FeedbackHeader, {
        closeOnClick: closeOnClick,
        children: headerText
      }), /*#__PURE__*/_jsx(StyledContent, {
        children: /*#__PURE__*/_jsx(FeedbackContent, {
          type: currentStep.type,
          setRating: setRating,
          feedback: feedback,
          setFeedback: setFeedback,
          completeSurvey: completeSurvey,
          setContactConsent: setContactConsent,
          contactConsent: contactConsent,
          incrementSurveyStep: incrementSurveyStep,
          consentTextKey: consentTextKey,
          hideIllustration: hideIllustration
        })
      })]
    })
  });
};
export default FeedbackModal;